import { InputAdornment, TextFieldProps } from '@mui/material';
import FormField, { FormFieldProps } from 'components/FormFields/FormField';
import { NoControllerTextField } from 'components/FormFields/FormNumberFieldWithControls/styles';
import { currenciesMap } from 'lib/constants/currencies';
import useFieldFormError from 'lib/form/useFieldFormError';
import { useGetOrganizationCurrencyQuery } from 'lib/graphql/graphql';
import useEvent from 'providers/event/useEvent';
import { useMemo } from 'react';
import {
  FieldValues,
  RegisterOptions,
  useController,
  ValidationRule,
} from 'react-hook-form';
import { useIntl } from 'react-intl';

type CustomMUITextFieldProps = Omit<TextFieldProps, 'onChange' | 'onBlur' | 'ref' | 'error' | 'required' | 'name' | 'inputProps' | 'type'>;

interface FormMoneyFieldProps extends FormFieldProps, CustomMUITextFieldProps {
  minimum?: ValidationRule<GLfloat>
  maximum?: ValidationRule<number>
  innerFieldLabel?: string
  rules?: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'min' | 'max'>
}

function FormMoneyField(props: FormMoneyFieldProps) {
  const {
    labelId,
    maximum,
    minimum,
    name,
    required,
    innerFieldLabel,
    fieldDescription,
    rules = {},
    ...textFieldProps
  } = props;
  const intl = useIntl();
  const { organizationId } = useEvent();

  const { data } = useGetOrganizationCurrencyQuery({ variables: { id: organizationId } });
  const currency = data?.getOrganizationDetails?.currency;

  const newRules = useMemo(
    () => ({ min: minimum, max: maximum, ...rules }),
    [minimum, maximum, rules],
  );

  const { field, formState } = useController({ name, rules: { required, ...newRules } });
  const error = useFieldFormError(name);

  return (
    <FormField
      name={name}
      labelId={labelId}
      required={required}
      fieldDescription={fieldDescription}
    >
      <NoControllerTextField
        size="small"
        fullWidth
        defaultValue={field.value || formState.defaultValues?.[name] || ''}
        label={innerFieldLabel ? intl.formatMessage({ id: innerFieldLabel }) : ''}
        onChange={(event) => field.onChange(+event.target.value as number)}
        onBlur={field.onBlur}
        ref={field.ref}
        error={Boolean(error?.type) || false}
        type="number"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {currency && currenciesMap[currency].symbol}
            </InputAdornment>
          ),
          inputProps: { min: 0 },
        }}
        {...textFieldProps}
      />
    </FormField>
  );
}

export default FormMoneyField;
